import { hero, persist, setHero, startStory, toHub } from "../app.ts";
import { sfx } from "../audio.ts";
import { bird } from "../data/story.ts";
import { now, setScene } from "../engine.ts";
import { Actor } from "../render/actor.ts";
import { drawArena, GROUND, W } from "../render/arena.ts";
import { BASE_STAT, derive, emptyStats, newHero, START_POINTS, STAT_INFO } from "../rules.ts";
import { STAT_KEYS, type BirdLook, type Stats } from "../types.ts";
import { btn, h, mount } from "../ui/dom.ts";
import { muteButton } from "./common.ts";
import { titleScreen } from "./title.ts";

type Mode = "new" | "parlor" | "train";

const PLUMAGE = ["#f5f1e6", "#f2e2b8", "#e8b04a", "#e3a01b", "#c9853a", "#a64b2a", "#7a4a2a", "#2b2b33", "#7d8a99", "#b9a6d6", "#f2a0b5", "#3b6fd6", "#3f7f4f"];
const COMBS = ["#d8342c", "#ff5a8a", "#ff8c1a", "#8a2be2", "#2f6fd6", "#222222"];
const BEAKS = ["#f2b134", "#f08a24", "#e8d36c", "#9aa3ad", "#f2a0b5", "#2b2b2b"];
const FLAIR = ["#d32f2f", "#2f6fd6", "#2e7d32", "#f5c431", "#8a2be2", "#111111"];

const NAMES = [
  "Spartacluck", "Maximus Peckimus", "Cluckius Caesar", "Napoleggon", "Joan of Bawk", "Cleopecktra", "Alexander the Grain",
  "Hennibal", "Boudicluck", "Eggward the Conqueror", "Feathercules", "Peckahontas", "Charlemaize", "Hen Solo-Cup", "Ramses the Rooster",
];

interface Choice<T> {
  label: string;
  value: T;
}

const COMB_STYLES: Choice<BirdLook["combStyle"]>[] = [
  { label: "Single Comb", value: "single" },
  { label: "Rose Comb", value: "rose" },
  { label: "Pom-Pom Crest", value: "crest" },
  { label: "Punk Mohawk", value: "mohawk" },
  { label: "Bald & Proud", value: "none" },
];
const TAILS: Choice<BirdLook["tailStyle"]>[] = [
  { label: "Fluffy", value: "fluffy" },
  { label: "Rooster Sickles", value: "sickle" },
  { label: "Turkey Fan", value: "fan" },
  { label: "Stubby", value: "stub" },
];
const PATTERNS: Choice<BirdLook["pattern"]>[] = [
  { label: "Solid", value: "solid" },
  { label: "Speckled", value: "speckled" },
  { label: "Barred", value: "barred" },
  { label: "Laced", value: "laced" },
];
const EYES: Choice<BirdLook["eyes"]>[] = [
  { label: "Bright", value: "normal" },
  { label: "Fierce", value: "fierce" },
  { label: "Sleepy", value: "sleepy" },
  { label: "Cool Shades", value: "shades" },
];
const EXTRAS: Choice<BirdLook["extra"]>[] = [
  { label: "None", value: "none" },
  { label: "Bandana", value: "bandana" },
  { label: "Eyepatch", value: "eyepatch" },
  { label: "Battle Scar", value: "scar" },
  { label: "Monocle", value: "monocle" },
  { label: "Mustache", value: "mustache" },
  { label: "Bow Tie", value: "bowtie" },
];
const BUILDS: Choice<number>[] = [
  { label: "Bantam", value: 0.9 },
  { label: "Standard", value: 1 },
  { label: "Brahma", value: 1.12 },
];

const pick = <T,>(a: readonly T[]): T => a[Math.floor(Math.random() * a.length)] as T;

function randomLook(): BirdLook {
  return bird({
    body: pick(PLUMAGE),
    wing: pick(PLUMAGE),
    tail: pick(PLUMAGE),
    comb: pick(COMBS),
    beak: pick(BEAKS),
    combStyle: pick(COMB_STYLES).value,
    tailStyle: pick(TAILS).value,
    pattern: pick(PATTERNS).value,
    eyes: pick(EYES).value,
    extra: pick(EXTRAS).value,
    extraColor: pick(FLAIR),
    size: pick(BUILDS).value,
  });
}

export function creatorScreen({ mode }: { mode: Mode }): void {
  const existing = mode === "new" ? null : hero();
  let name = existing?.name ?? pick(NAMES);
  let look: BirdLook = existing ? { ...existing.look } : bird({ tailStyle: "sickle", eyes: "fierce", extra: "bandana", extraColor: "#d32f2f" });
  const min: Stats = existing ? { ...existing.base } : emptyStats(BASE_STAT);
  const stats: Stats = { ...min };
  let points = existing ? existing.points : START_POINTS;

  const preview = new Actor(look, existing?.gear ?? { weapon: "twig" }, 1, 300);
  const backdrop = mode === "train" ? "colosseum" : "barn";
  setScene((ctx, t) => {
    drawArena(ctx, backdrop, t, 0.3);
    const g = ctx.createRadialGradient(300, 480, 40, 300, 480, 420);
    g.addColorStop(0, "rgba(255,240,200,0)");
    g.addColorStop(1, "rgba(15,8,4,0.6)");
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, W, 720);
    preview.draw(ctx, t, GROUND + 20, 2.7);
  });

  const nameLabel = h("div", { class: "name" }, name);
  const label = h("div", { class: "preview-label" }, nameLabel);

  function changed(): void {
    preview.look = look;
    nameLabel.textContent = name || "???";
    void preview.play("taunt", now());
    sfx.cluck();
  }

  // ---------- look step ----------

  function swatches(colors: string[], get: () => string, set: (c: string) => void): HTMLElement {
    const wrap = h("div", { class: "swatches" });
    const render = () => {
      wrap.replaceChildren(
        ...colors.map((c) =>
          h("button", {
            class: `swatch${get() === c ? " on" : ""}`,
            style: { background: c },
            title: c,
            onclick: () => {
              set(c);
              render();
              changed();
            },
          }),
        ),
      );
    };
    render();
    return wrap;
  }

  function picker<T>(choices: Choice<T>[], get: () => T, set: (v: T) => void): HTMLElement {
    const val = h("div", { class: "val" });
    const idx = () => Math.max(0, choices.findIndex((c) => c.value === get()));
    const show = () => (val.textContent = choices[idx()]?.label ?? "");
    const step = (d: number) => {
      const next = choices[(idx() + d + choices.length) % choices.length];
      if (next) set(next.value);
      show();
      changed();
    };
    show();
    return h("div", { class: "picker" }, btn("◀", () => step(-1), "small"), val, btn("▶", () => step(1), "small"));
  }

  const opt = (labelText: string, control: HTMLElement) => h("div", { class: "opt" }, h("label", null, labelText), control);

  function lookStep(): void {
    const nameInput = h("input", {
      type: "text",
      value: name,
      maxLength: 22,
      oninput: (e: Event) => {
        if (e.target instanceof HTMLInputElement) name = e.target.value;
        nameLabel.textContent = name || "???";
      },
    });
    const set = <K extends keyof BirdLook>(k: K) => (v: BirdLook[K]) => (look = { ...look, [k]: v });
    const get = <K extends keyof BirdLook>(k: K) => () => look[k];
    const panel = h(
      "div",
      { class: "creator panel" },
      h("div", { class: "row" }, h("h2", { class: "grow" }, mode === "new" ? "Hatch Your Champion" : "Preening Parlor"), btn("🎲 Randomize", () => {
        look = randomLook();
        name = mode === "new" ? pick(NAMES) : name;
        nameInput.value = name;
        lookStep();
        changed();
      }, "small blue")),
      opt(
        "Name",
        h("div", { class: "row" }, nameInput, btn("🎲", () => {
          name = pick(NAMES);
          nameInput.value = name;
          changed();
        }, "small wood")),
      ),
      opt("Plumage", swatches(PLUMAGE, get("body"), set("body"))),
      opt("Wings", swatches(PLUMAGE, get("wing"), set("wing"))),
      opt("Tail Color", swatches(PLUMAGE, get("tail"), set("tail"))),
      opt("Comb Color", swatches(COMBS, get("comb"), set("comb"))),
      opt("Beak & Feet", swatches(BEAKS, () => look.beak, (c) => (look = { ...look, beak: c, legs: c }))),
      h(
        "div",
        { class: "two" },
        opt("Comb", picker(COMB_STYLES, get("combStyle"), set("combStyle"))),
        opt("Tail", picker(TAILS, get("tailStyle"), set("tailStyle"))),
        opt("Pattern", picker(PATTERNS, get("pattern"), set("pattern"))),
        opt("Eyes", picker(EYES, get("eyes"), set("eyes"))),
        opt("Flair", picker(EXTRAS, get("extra"), set("extra"))),
        opt("Build", picker(BUILDS, get("size"), set("size"))),
      ),
      opt("Flair Color", swatches(FLAIR, () => look.extraColor ?? "", set("extraColor"))),
      h("div", { class: "grow" }),
      h(
        "div",
        { class: "row", style: { justifyContent: "space-between" } },
        mode === "new" ? btn("◀ Title", titleScreen, "wood") : btn("Cancel", toHub, "wood"),
        mode === "new"
          ? btn("Train Stats ▶", () => {
              name = name.trim() || "Nameless Nugget";
              statStep();
            }, "green big")
          : btn("Looking Good!", () => {
              const me = hero();
              me.look = look;
              me.name = name.trim() || me.name;
              persist();
              toHub();
            }, "green big"),
      ),
    );
    mount(null, label, panel, muteButton());
  }

  // ---------- stat step ----------

  function statStep(): void {
    const pointsEl = h("b");
    const derived = h("div", { class: "row", style: { gap: "18px", fontWeight: "900", fontSize: "18px" } });
    const rows = STAT_KEYS.map((k) => {
      const v = h("div", { class: "v" });
      const minus = btn("−", () => adjust(-1), "small red");
      const plus = btn("+", () => adjust(1), "small green");
      const adjust = (d: number) => {
        if (d > 0 && points <= 0) return;
        if (d < 0 && stats[k] <= min[k]) return;
        stats[k] += d;
        points -= d;
        refresh();
      };
      const row = h(
        "div",
        { class: "stat" },
        h("div", null, h("div", { class: "name" }, STAT_INFO[k].name)),
        h("div", { class: "desc" }, STAT_INFO[k].desc),
        v,
        h("div", { class: "ctl" }, minus, plus),
      );
      return { k, v, minus, plus, row };
    });

    function refresh(): void {
      pointsEl.textContent = String(points);
      for (const r of rows) {
        r.v.textContent = String(stats[r.k]);
        r.v.classList.toggle("up", stats[r.k] > min[r.k]);
        r.minus.disabled = stats[r.k] <= min[r.k];
        r.plus.disabled = points <= 0;
      }
      const d = derive(stats, existing?.gear ?? {});
      derived.replaceChildren(h("span", null, `❤️ Health ${d.maxHp}`), h("span", null, `⚡ Energy ${d.maxEn}`));
      confirmBtn.disabled = mode === "new" && points > 0;
    }

    const confirmBtn =
      mode === "new"
        ? btn("Begin the Uprising! ▶", () => {
            setHero(newHero(name, look, { ...stats }));
            startStory();
          }, "red big")
        : btn("Done", () => {
            const me = hero();
            me.base = { ...stats };
            me.points = points;
            persist();
            toHub();
          }, "green big");

    const panel = h(
      "div",
      { class: "creator panel" },
      h("h2", null, mode === "new" ? "Train Your Champion" : "Training Grounds"),
      h("div", { class: "points" }, "Points to spend: ", pointsEl),
      h("div", { class: "stats" }, ...rows.map((r) => r.row)),
      derived,
      h("div", { class: "grow" }),
      h(
        "div",
        { class: "row", style: { justifyContent: "space-between" } },
        mode === "new" ? btn("◀ Looks", lookStep, "wood") : btn("Cancel", toHub, "wood"),
        confirmBtn,
      ),
    );
    refresh();
    mount(null, label, panel, muteButton());
  }

  if (mode === "train") statStep();
  else lookStep();
}
